import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../lib/api';
import { formatStripeError } from '../lib/stripeErrors';
import { InAppBrowserWarning } from '../components/InAppBrowserWarning';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast, Toaster } from 'sonner';
import { CreditCard, Shield, CheckCircle, Lock, AlertCircle, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51Suz7mBND4x5eqTY4lK8jJnCzQu1eZ5nDVe0LK8TmvKdqN0DVQjfEJBEgLaIy9rGLH7VGKJqRVoZPn7nYUHtjEVH00xzP2BXKA');

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#6b7280' },
      iconColor: '#ff007f',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

const CardForm = ({ bookingId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initSetupIntent();
  }, [bookingId]);

  const initSetupIntent = async () => {
    try {
      const res = await api.post('/payments/setup-card-for-booking', { booking_id: bookingId });
      setClientSecret(res.data.client_secret);
    } catch (err) {
      console.error('Failed to init setup intent:', err);
      setError(err.response?.data?.detail || 'Failed to initialize card setup');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (stripeError) {
        setError(formatStripeError(stripeError));
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        const confirmRes = await api.post('/payments/confirm-card-for-booking', {
          booking_id: bookingId,
          payment_method_id: setupIntent.payment_method
        });
        toast.success('Card saved successfully!');
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <InAppBrowserWarning />
      <div className="p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
        <CardElement options={cardElementOptions} />
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-white/50 text-sm">
        <Lock className="w-4 h-4" />
        <span>Your card details are securely processed by Stripe</span>
      </div>

      <Button
        type="submit"
        disabled={!stripe || !clientSecret || loading}
        className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 h-12"
        data-testid="save-card-btn"
      >
        {loading ? (
          <>
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
            Saving Card...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Save Card
          </>
        )}
      </Button>
    </form>
  );
};

const AddCardForBooking = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    } else {
      setError('No booking ID provided');
      setLoading(false);
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const res = await api.get(`/payments/booking-for-card/${bookingId}`);
      setBooking(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (data) => {
    setSuccess(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="container mx-auto px-4 py-20 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="container mx-auto px-4 py-12 max-w-lg">
          <Card className="bg-[#111] border-red-500/30">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-white text-xl font-semibold mb-2">Error</h2>
              <p className="text-white/60">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="container mx-auto px-4 py-12 max-w-lg">
          <Card className="bg-[#111] border-green-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-white text-2xl font-semibold mb-2">Card Saved Successfully!</h2>
              <p className="text-white/60 mb-4">
                Thank you! Your card has been saved securely. The school will process your payment shortly.
              </p>
              <div className="bg-[#1a1a1a] rounded-lg p-4 text-left">
                <p className="text-white/50 text-sm">Booking Details:</p>
                <p className="text-white font-medium">{booking?.student_name}</p>
                <p className="text-white/70">{booking?.class_names?.join(', ') || booking?.class_name}</p>
                <p className="text-[#FF007F] font-semibold mt-2">£{booking?.amount?.toFixed(2)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="container mx-auto px-4 py-12 max-w-lg">
        <Card className="bg-[#111] border-[#333]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#FF007F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-[#FF007F]" />
            </div>
            <CardTitle className="text-white text-2xl">Complete Your Enrollment</CardTitle>
            <p className="text-white/60 mt-2">
              Add your payment card to complete the enrollment for {booking?.student_name}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Booking Summary */}
            <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#333]">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#FF007F]" />
                <span className="text-white/70 text-sm font-medium">Enrollment Summary</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/60">Student:</span>
                  <span className="text-white font-medium">{booking?.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Classes:</span>
                  <span className="text-white text-right text-sm max-w-[200px]">
                    {booking?.class_names?.join(', ') || booking?.class_name}
                  </span>
                </div>
                <div className="border-t border-[#333] pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="text-white font-medium">Total:</span>
                    <span className="text-[#FF007F] font-bold text-lg">£{booking?.amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <Elements stripe={stripePromise}>
              <CardForm bookingId={bookingId} onSuccess={handleSuccess} />
            </Elements>

            {/* Security badges */}
            <div className="flex items-center justify-center gap-4 pt-4 border-t border-[#333]">
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Shield className="w-4 h-4" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Lock className="w-4 h-4" />
                <span>PCI Compliant</span>
              </div>
            </div>

            <p className="text-white/40 text-xs text-center">
              Your card will be saved securely. Payment will only be taken when authorized by UPA.
            </p>
          </CardContent>
        </Card>
      </div>
      <Toaster position="top-center" />
    </div>
  );
};

export default AddCardForBooking;
